import { Card, CardContent, CardHeader, LinearProgress, Typography, Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';

interface ApiStat {
  name: string;
  plan?: string;
  used: number;
  limit: number;
  remaining?: number;
  resetTime?: string;
  rateLimit?: string;
  dailyLimit?: number;
  tokens?: {
    used: number;
    limit: number;
    remaining: number;
    resetTime?: string;
  };
  note?: string;
  dashboardUrl?: string;
  percentage?: string;
}

export const ApiMonitor = () => {
  const [stats, setStats] = useState<ApiStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dataProvider = useDataProvider();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'https://admin-markets.moliedev.xyz/api';
        const token = localStorage.getItem('token');

        const response = await fetch(`${apiUrl}/api-stats`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch API stats');
        }

        const data = await response.json();
        setStats(data);
      } catch (err) {
        console.error('Error fetching API stats:', err);
        setError('Failed to load API statistics. Please check your connection and authorization.');
        setStats([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, []);

  const getPercentage = (used: number, limit: number) => {
    if (limit === 0) return 0;
    return (used / limit) * 100;
  };

  const getColor = (percentage: number) => {
    if (percentage < 50) return 'success';
    if (percentage < 80) return 'warning';
    return 'error';
  };

  const formatResetTime = (time?: string) => {
    if (!time) return 'N/A';

    // Handle string format like "15.933s" from OpenAI headers
    if (time.includes('s') && !time.includes('T')) {
      const seconds = parseFloat(time.replace('s', ''));
      if (seconds < 60) {
        return `${Math.ceil(seconds)}s`;
      }
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.ceil(seconds % 60);
      return `${minutes}m ${remainingSeconds}s`;
    }

    // Handle ISO date format
    const date = new Date(time);
    const now = new Date();
    const diff = date.getTime() - now.getTime();

    // If already reset or resetting soon
    if (diff <= 0) {
      return 'Resetting...';
    }

    // Format time remaining
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader title="API Limits Monitor" />
        <CardContent>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader title="API Limits Monitor" />
        <CardContent>
          <Typography color="error">{error}</Typography>
        </CardContent>
      </Card>
    );
  }

  if (stats.length === 0) {
    return (
      <Card>
        <CardHeader title="API Limits Monitor" />
        <CardContent>
          <Typography color="text.secondary">No API statistics available.</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="API Limits Monitor"
        subheader="Monitor your API usage and limits"
      />
      <CardContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
          {stats.map((stat) => {
            const percentage = getPercentage(stat.used, stat.limit);
            const color = getColor(percentage);

            return (
              <Card variant="outlined" key={stat.name}>
                <CardContent>
                  {/* API Name and Plan */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      {stat.name}
                    </Typography>
                    {stat.plan && (
                      <Typography
                        variant="caption"
                        sx={{
                          px: 1,
                          py: 0.5,
                          bgcolor: 'primary.light',
                          color: 'primary.contrastText',
                          borderRadius: 1,
                          fontWeight: 'bold'
                        }}
                      >
                        {stat.plan}
                      </Typography>
                    )}
                  </Box>

                  {/* Rate Limit Info (if available) */}
                  {stat.rateLimit && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      📊 {stat.rateLimit}
                    </Typography>
                  )}

                  {/* Requests Usage */}
                  {stat.limit > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {stat.tokens ? 'Requests' : 'Usage'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {stat.used.toLocaleString()} / {stat.limit.toLocaleString()}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={percentage}
                        color={color}
                        sx={{ height: 10, borderRadius: 5 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        {percentage.toFixed(1)}% used
                      </Typography>
                    </Box>
                  )}

                  {/* Daily Limit (if different from main limit) */}
                  {stat.dailyLimit && stat.dailyLimit !== stat.limit && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      📅 Daily limit: {stat.dailyLimit.toLocaleString()} requests
                    </Typography>
                  )}

                  {/* Tokens Usage (if available) */}
                  {stat.tokens && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Tokens
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {stat.tokens.used.toLocaleString()} / {stat.tokens.limit.toLocaleString()}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(stat.tokens.used / stat.tokens.limit) * 100}
                        color={getColor((stat.tokens.used / stat.tokens.limit) * 100)}
                        sx={{ height: 10, borderRadius: 5 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        {((stat.tokens.used / stat.tokens.limit) * 100).toFixed(1)}% used
                      </Typography>
                    </Box>
                  )}

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Resets in: {formatResetTime(stat.resetTime)}
                  </Typography>

                  {/* Additional note */}
                  {stat.note && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mt: 1 }}>
                      {stat.note}
                    </Typography>
                  )}

                  {/* Dashboard link */}
                  {stat.dashboardUrl && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
                      <a
                        href={stat.dashboardUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          textDecoration: 'none',
                          color: '#1976d2',
                          fontSize: '0.875rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        📊 View Dashboard
                      </a>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};